/**
 * SSE React Context — the bridge between :func:`useSse` (owned by
 * :func:`SseProvider`) and consumers across the React tree
 * (:class:`ConnectionStatusIndicator` in the top bar; the
 * :class:`SnapshotLayer` consuming live deltas).
 *
 * Stream A PR 2 / commit M3 — split context-decl from
 * provider-impl per the fast-refresh discipline
 * (``react-refresh/only-export-components``). Mirrors the auth
 * module's :file:`session-context.ts` / :file:`session-provider.tsx`
 * pattern.
 *
 * # Why two contexts
 *
 * The SSE provider lives above the AppShell so the top-bar's
 * :class:`ConnectionStatusIndicator` reads the connection state.
 * But the SSE's bbox subscription is driven by the Map's viewport,
 * and the Map renders inside the AppShell's :class:`Outlet` — below
 * the provider. The bridge is :class:`MapViewportContext`: the Map
 * pushes its bounds via :func:`useSetMapViewport`; the provider
 * reads via :func:`useMapViewport` and threads the value into
 * :func:`useSse`.
 *
 * Why not Zustand for this single piece of cross-tree state? The
 * surface is one value + one setter that lives strictly within the
 * Map-and-Chrome tree; React Context is the right tool. Zustand
 * (REQ-UI-027) becomes the right tool when multiple panes
 * subscribe (Stream C's multi-pane workspace).
 *
 * Cross-references:
 *   - REQ-UI-006 / REQ-UI-008 / REQ-UI-009 (the state surface
 *     exposed by this context).
 *   - docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md
 *     §"Connection-state machine".
 *   - ADR-016 §1 + §3.
 */
import { createContext, useContext } from "react";
import type { SseConnection, SseViewport } from "@/map/use-sse";

/**
 * The SSE connection context value mirrors :type:`SseConnection`.
 * ``null`` while the provider hasn't mounted (consumers outside the
 * provider tree see ``null`` and the hook throws).
 *
 * Exposed on its own line so the tree's structure stays close to
 * :file:`auth/session-context.ts`'s shape.
 */
export const SseContext = createContext<SseConnection | null>(null);

/**
 * The map-viewport bridge context. Holds the current viewport +
 * a setter. ``viewport`` is ``null`` before the map's first
 * ``onMapReady`` callback fires.
 *
 * Cross-references:
 *   - REQ-UI-007 (viewport-change → BFF subscribe).
 */
export interface MapViewportContextValue {
  viewport: SseViewport | null;
  setViewport: (next: SseViewport | null) => void;
}

export const MapViewportContext = createContext<MapViewportContextValue | null>(null);

/**
 * Access the SSE connection state from any descendant of
 * :func:`SseProvider`.
 *
 * Throws if called outside the provider — operator-visibility per
 * CLAUDE.md §8.1: missing context is a developer error and should
 * fail loudly rather than silently degrading to a fake "live" state.
 *
 * @throws Error when the calling component is not wrapped in
 *     :func:`SseProvider`.
 *
 * Cross-references:
 *   - REQ-UI-008.
 */
export function useSseConnection(): SseConnection {
  const ctx = useContext(SseContext);
  if (ctx === null) {
    throw new Error(
      "useSseConnection() called outside a <SseProvider>. " +
        "Wrap the consumer's component tree per ui/src/main.tsx.",
    );
  }
  return ctx;
}

/**
 * Convenience accessor for the state slice alone — used by
 * :class:`ConnectionStatusIndicator` which reads state + derives the
 * LIVE-IDLE pulse from ``lastEventTime``.
 */
export function useSseState() {
  return useSseConnection().state;
}

/**
 * Access the current map viewport bridge. Returns the viewport +
 * the setter. Throws if called outside :func:`SseProvider`.
 *
 * Cross-references:
 *   - REQ-UI-007.
 */
export function useMapViewport(): MapViewportContextValue {
  const ctx = useContext(MapViewportContext);
  if (ctx === null) {
    throw new Error(
      "useMapViewport() called outside a <SseProvider>. " +
        "Wrap the consumer's component tree per ui/src/main.tsx.",
    );
  }
  return ctx;
}

/**
 * Convenience accessor for the viewport setter alone — used by
 * :class:`Map` which pushes its bounds on the ``onMapReady`` callback
 * (and, when M4 lands pan-debounce, on bounds-change).
 */
export function useSetMapViewport(): (next: SseViewport | null) => void {
  return useMapViewport().setViewport;
}
